import React from 'react'
import { Link } from 'react-router-dom'
import {
  Users,
  Building2,
  FileText,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  ArrowRight,
  Zap,
  Shield,
  Activity
} from 'lucide-react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { formatCurrency, formatNumber } from '../../data/mockData'

const platformStats = [
  { name: 'Active Creators', value: '2,847', change: '+124 this month', icon: Users, color: 'bg-teal-500' },
  { name: 'Active Brands', value: '156', change: '+12 this month', icon: Building2, color: 'bg-blue-500' },
  { name: 'Live Campaigns', value: '48', change: '23 in review', icon: FileText, color: 'bg-purple-500' },
  { name: 'GMV This Month', value: '$1.2M', change: '+28% vs last month', icon: DollarSign, color: 'bg-green-500' },
]

const gmvData = [
  { month: 'Aug', gmv: 650000 },
  { month: 'Sep', gmv: 780000 },
  { month: 'Oct', gmv: 890000 },
  { month: 'Nov', gmv: 950000 },
  { month: 'Dec', gmv: 1100000 },
  { month: 'Jan', gmv: 1250000 },
]

const campaignsByStatus = [
  { name: 'In Progress', value: 23, color: '#10b981' },
  { name: 'Content Review', value: 12, color: '#f59e0b' },
  { name: 'Matching', value: 8, color: '#8b5cf6' },
  { name: 'Draft', value: 5, color: '#6b7280' },
]

const pendingActions = [
  { id: 1, type: 'brand_approval', title: 'HealthFirst Pharma', description: 'New brand application pending approval', time: '2 hours ago', icon: Building2 },
  { id: 2, type: 'creator_verification', title: 'Dr. Michael Chen', description: 'License verification required', time: '4 hours ago', icon: Users },
  { id: 3, type: 'compliance_review', title: 'Spring Wellness Launch', description: '3 deliverables pending compliance check', time: '5 hours ago', icon: Shield },
  { id: 4, type: 'matching', title: 'New Product Announcement', description: 'Ready for creator matching', time: '1 day ago', icon: Zap },
  { id: 5, type: 'dispute', title: 'Heart Health Campaign', description: 'Payment dispute raised by creator', time: '1 day ago', icon: AlertCircle },
]

const recentCampaigns = [
  { id: '1', name: 'Spring Wellness Launch', brand: 'MedTech Innovations', status: 'in_progress', gmv: 75000, creators: 8 },
  { id: '2', name: 'Patient Education Series', brand: 'MedTech Innovations', status: 'content_review', gmv: 50000, creators: 5 },
  { id: '3', name: 'Heart Health Awareness', brand: 'CardioFirst', status: 'matching', gmv: 35000, creators: 0 },
  { id: '4', name: 'Digital Health Summit', brand: 'TechMed Conference', status: 'funded', gmv: 100000, creators: 10 },
]

function AdminDashboard() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-500">Monitor platform health and pending actions</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
          <Activity className="w-4 h-4" />
          All Systems Operational
        </div>
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {platformStats.map((stat) => (
          <div key={stat.name} className="stat-card">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-sm text-gray-500">{stat.name}</p>
            <p className="text-xs text-green-600 mt-1">{stat.change}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* GMV Chart */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">GMV Trend</h2>
            <select className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white">
              <option>Last 6 months</option>
              <option>Last 12 months</option>
              <option>This year</option>
            </select>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={gmvData}>
                <defs>
                  <linearGradient id="colorGMV" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(val) => `$${val/1000000}M`} />
                <Tooltip
                  formatter={(value) => [`$${(value/1000000).toFixed(2)}M`, 'GMV']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                />
                <Area
                  type="monotone"
                  dataKey="gmv"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorGMV)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Campaign Status Breakdown */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Campaign Status</h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={campaignsByStatus}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {campaignsByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-4">
            {campaignsByStatus.map((item) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: item.color }} />
                  <span className="text-gray-600">{item.name}</span>
                </div>
                <span className="font-medium text-gray-900">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pending Actions */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Pending Actions</h2>
            <span className="badge bg-red-100 text-red-700">{pendingActions.length} items</span>
          </div>
          <div className="space-y-3">
            {pendingActions.map((action) => (
              <div key={action.id} className="flex items-start p-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${
                  action.type === 'dispute' ? 'bg-red-100' :
                  action.type === 'compliance_review' ? 'bg-yellow-100' : 'bg-hf-teal-100'
                }`}>
                  <action.icon className={`w-5 h-5 ${
                    action.type === 'dispute' ? 'text-red-600' :
                    action.type === 'compliance_review' ? 'text-yellow-600' : 'text-hf-teal-600'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{action.title}</p>
                  <p className="text-sm text-gray-500 truncate">{action.description}</p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{action.time}</span>
              </div>
            ))}
          </div>
          <button className="w-full mt-4 text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium">
            View all pending actions
          </button>
        </div>

        {/* Recent Campaigns */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Campaigns</h2>
            <Link to="/admin/campaigns" className="text-sm text-hf-teal-600 hover:text-hf-teal-700 font-medium flex items-center">
              View all
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 text-xs font-medium text-gray-500">Campaign</th>
                  <th className="text-left py-2 text-xs font-medium text-gray-500">Status</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500">GMV</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentCampaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50">
                    <td className="py-3">
                      <p className="font-medium text-gray-900 text-sm">{campaign.name}</p>
                      <p className="text-xs text-gray-500">{campaign.brand}</p>
                    </td>
                    <td className="py-3">
                      <span className={`badge ${
                        campaign.status === 'in_progress' ? 'bg-green-100 text-green-700' :
                        campaign.status === 'content_review' ? 'bg-yellow-100 text-yellow-700' :
                        campaign.status === 'matching' ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {campaign.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3 text-right font-medium text-gray-900 text-sm">
                      {formatCurrency(campaign.gmv)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-4 gap-4">
        <Link
          to="/admin/matching"
          className="card hover:border-purple-200 hover:shadow-md transition-all group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <Zap className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Matching Queue</p>
              <p className="text-sm text-gray-500">8 pending</p>
            </div>
          </div>
        </Link>
        <Link
          to="/admin/compliance"
          className="card hover:border-yellow-200 hover:shadow-md transition-all group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center group-hover:bg-yellow-200 transition-colors">
              <Shield className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Compliance</p>
              <p className="text-sm text-gray-500">12 reviews</p>
            </div>
          </div>
        </Link>
        <Link
          to="/admin/analytics"
          className="card hover:border-blue-200 hover:shadow-md transition-all group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Analytics</p>
              <p className="text-sm text-gray-500">View reports</p>
            </div>
          </div>
        </Link>
        <Link
          to="/admin/users"
          className="card hover:border-teal-200 hover:shadow-md transition-all group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center group-hover:bg-teal-200 transition-colors">
              <Users className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">User Management</p>
              <p className="text-sm text-gray-500">Manage users</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  )
}

export default AdminDashboard
